import { useIsAuthenticated } from "@azure/msal-react";
import {
  archiveResidenceType,
  publishResidenceType,
  ResidenceTypeModel,
  unpublishResidenceType,
  Visibility,
} from "../../clients/BackOfficeApi";
import {
  ViewOffIcon,
  DeleteIcon,
  RepeatIcon,
  ViewIcon,
} from "@chakra-ui/icons";
import { Flex } from "@chakra-ui/react";
import { useCallback } from "react";
import { ActionButton } from "../../elements/ActionButton";

interface Props {
  residenceType: ResidenceTypeModel;
  forCell?: boolean;
  onChange?: () => void;
}
export function ResidenceTypeActions({
  residenceType,
  forCell = false,
  onChange = () => {},
}: Props) {
  const isAuthenticated = useIsAuthenticated();

  const buttonVariant = forCell ? "link" : "solid";
  const direction = forCell ? "column" : "row";

  const publish = useCallback(() => {
    if (!isAuthenticated) return;

    publishResidenceType({
      path: { residenceTypeId: residenceType.id },
      throwOnError: true,
    })
      .catch((e) => {
        console.error("Failed to publish residence type", e);
      })
      .finally(() => {
        onChange();
      });
  }, [isAuthenticated, onChange, residenceType.id]);

  const depublish = useCallback(() => {
    if (!isAuthenticated) return;

    unpublishResidenceType({
      path: { residenceTypeId: residenceType.id },
      throwOnError: true,
    })
      .catch(() => {})
      .finally(onChange);
  }, [isAuthenticated, onChange, residenceType.id]);

  const archive = useCallback(
    (confirmed: boolean) => {
      if (!isAuthenticated) return;

      if (!confirmed) return;

      archiveResidenceType({
        path: { residenceTypeId: residenceType.id },
        throwOnError: true,
      })
        .catch(() => {})
        .finally(onChange);
    },
    [isAuthenticated, onChange, residenceType.id],
  );

  const restore = useCallback(
    (confirmed: boolean) => {
      if (!isAuthenticated) return;

      if (!confirmed) return;

      unpublishResidenceType({
        path: { residenceTypeId: residenceType.id },
        throwOnError: true,
      })
        .catch(() => {})
        .finally(onChange);
    },
    [isAuthenticated, onChange, residenceType.id],
  );

  return (
    <Flex direction={direction} gap={2} minW={24}>
      {residenceType.visibility === Visibility.UNPUBLISHED &&
        residenceType.hasImage && (
          <ActionButton
            color="green.500"
            icon={<ViewIcon />}
            onClick={publish}
            title="Publiceren"
            variant={buttonVariant}
          />
        )}
      {residenceType.visibility === Visibility.PUBLISHED && (
        <ActionButton
          color="blue.500"
          icon={<ViewOffIcon />}
          onClick={depublish}
          title="Depubliceren"
          variant={buttonVariant}
        />
      )}
      {residenceType.visibility === Visibility.ARCHIVED && (
        <ActionButton
          color="gray.500"
          confirmDescription="Weet je zeker dat je dit woningtype wilt herstellen als concept?"
          confirmTitle="Dearchiveren bevestigen"
          icon={<RepeatIcon />}
          isDestructive
          onClick={restore}
          title="Herstellen als concept"
          variant={buttonVariant}
        />
      )}
      {residenceType.visibility === Visibility.UNPUBLISHED && (
        <ActionButton
          color="red.500"
          confirmDescription="Weet je zeker dat je dit woningtype wilt archiveren?"
          confirmTitle="Archiveren bevestigen"
          icon={<DeleteIcon />}
          isDestructive
          onClick={archive}
          title="Archiveren"
          variant={buttonVariant}
        />
      )}
    </Flex>
  );
}
